import { Web3Provider } from '@ethersproject/providers'
import { ChainId } from '@uniswap/sdk'
import { useWeb3React as useWeb3ReactCore } from '@web3-react/core'
import { Web3ReactContextInterface } from '@web3-react/core/dist/types'
import { useEffect, useState } from 'react'
import { injected } from '../connectors'
import { NetworkContextName } from '../constants'

export function useActiveWeb3React(): Web3ReactContextInterface<Web3Provider> & { chainId?: ChainId } {
  const context = useWeb3ReactCore<Web3Provider>()
  const contextNetwork = useWeb3ReactCore<Web3Provider>(NetworkContextName)

  // 如果用户钱包已连接且激活，使用用户钱包的上下文
  if (context.active) {
    return context
  }

  // 如果用户钱包已连接但未激活，仍然优先使用用户钱包的chainId
  // 但使用网络连接器的provider进行读取操作
  if (context.chainId && !context.active) {
    return {
      ...contextNetwork,
      chainId: context.chainId // 使用用户钱包的chainId
    }
  }

  // 最后回退到网络连接器
  return contextNetwork
}

// 确保 MetaMask provider 已激活的辅助函数
export async function ensureMetaMaskProvider(): Promise<boolean> {
  if (!window.ethereum) return false

  try {
    // 尝试激活 provider
    await (window.ethereum as any).request({ method: 'eth_chainId' })
    return true
  } catch (error) {
    console.error('Failed to activate MetaMask provider:', error)
    return false
  }
}

// 专门用于网络数据查询的钩子
export function useNetworkData(): Web3ReactContextInterface<Web3Provider> & { chainId?: ChainId } {
  const contextNetwork = useWeb3ReactCore<Web3Provider>(NetworkContextName)
  return contextNetwork
}

export function useEagerConnect() {
  const { activate, active } = useWeb3ReactCore() // specifically using useWeb3ReactCore because of what this hook does
  const [tried, setTried] = useState(false)

  useEffect(() => {
    const attemptConnection = async () => {
      try {
        // 检查是否有 ethereum 提供者
        if (window.ethereum) {
          // 1. 先激活 provider
          await (window.ethereum as any).request({ method: 'eth_chainId' })

          // 2. 尝试激活连接，不管是否已授权
          await activate(injected, undefined, true)
        }
      } catch (error) {
        // Eager connection failed
      } finally {
        setTried(true)
      }
    }

    attemptConnection()
  }, [activate]) // intentionally only running on mount (make sure it's only mounted once :))

  // if the connection worked, wait until we get confirmation of that to flip the flag
  useEffect(() => {
    if (active) {
      setTried(true)
    }
  }, [active])

  return tried
}

/**
 * Use for network and injected - logs user in
 * and out after checking what network theyre on
 */
export function useInactiveListener(suppress = false) {
  const { active, error, activate, deactivate } = useWeb3ReactCore() // specifically using useWeb3React because of what this hook does

  useEffect(() => {
    const { ethereum } = window

    if (ethereum && ethereum.on && !error && !suppress) {
      const handleChainChanged = () => {
        // 先完全断开连接
        deactivate()

        // 延迟后重新激活
        setTimeout(() => {
          activate(injected, undefined, true).catch(error => {
            // Failed to activate after chain changed
          })
        }, 1000)
      }

      const handleAccountsChanged = (accounts: string[]) => {
        if (accounts.length > 0) {
          // 先断开连接
          deactivate()

          // 延迟后重新激活
          setTimeout(() => {
            activate(injected, undefined, true).catch(error => {
              // Failed to activate after accounts changed
            })
          }, 1000) // 减少到1秒
        } else {
          // 没有账户时断开连接
          deactivate()
        }
      }

      // 监听连接状态变化
      const handleConnect = () => {
        setTimeout(() => {
          activate(injected, undefined, true).catch(error => {
            // Failed to activate after connect
          })
        }, 1000)
      }

      // 监听断开连接
      const handleDisconnect = () => {
        deactivate()
      }

      ethereum.on('chainChanged', handleChainChanged)
      ethereum.on('accountsChanged', handleAccountsChanged)
      ethereum.on('connect', handleConnect)
      ethereum.on('disconnect', handleDisconnect)

      return () => {
        if (ethereum.removeListener) {
          ethereum.removeListener('chainChanged', handleChainChanged)
          ethereum.removeListener('accountsChanged', handleAccountsChanged)
          ethereum.removeListener('connect', handleConnect)
          ethereum.removeListener('disconnect', handleDisconnect)
        }
      }
    }
    return undefined
  }, [active, error, suppress, activate, deactivate])
}
